import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Alert,
  CircularProgress, Card, CardContent, CardActionArea,
  Divider, Badge, ToggleButtonGroup, ToggleButton,
  TextField, InputAdornment, Tooltip, IconButton, Snackbar,
} from '@mui/material';
import {
  Inbox, Refresh, FilterList, BeachAccess, Gavel, Description,
  SupportAgent, School, Receipt, ErrorOutline, CheckCircle,
  Warning, Info, NavigateNext, Circle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiFetch';

interface ActionItem {
  id: string;
  category: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  count?: number;
  daysUntil?: number;
  linkPath: string;
  employeeName?: string;
  employeeId?: string;
  createdAt?: string;
}

interface InboxData {
  items: ActionItem[];
  summary: Record<string, number>;
}

const PRIORITY_CONFIG = {
  urgent: { color: '#d32f2f', bg: '#ffebee', label: 'Urgent',  chip: 'error'   as const },
  high:   { color: '#f57c00', bg: '#fff3e0', label: 'High',    chip: 'warning' as const },
  medium: { color: '#1976d2', bg: '#e3f2fd', label: 'Medium',  chip: 'primary' as const },
  low:    { color: '#757575', bg: '#f5f5f5', label: 'Low',     chip: 'default' as const },
};

const CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  'Leave Requests':   { icon: <BeachAccess />,    color: '#1976d2' },
  'Probation Reviews':{ icon: <Gavel />,          color: '#7b1fa2' },
  'Contract Renewals':{ icon: <Description />,    color: '#f57c00' },
  'Document Expiry':  { icon: <ErrorOutline />,   color: '#d32f2f' },
  'Helpdesk':         { icon: <SupportAgent />,   color: '#00796b' },
  'Onboarding':       { icon: <School />,         color: '#388e3c' },
  'Expense Reports':  { icon: <Receipt />,        color: '#5d4037' },
  'Data Gaps':        { icon: <Info />,           color: '#9e9e9e' },
};

function PriorityIcon({ p }: { p: ActionItem['priority'] }) {
  switch (p) {
    case 'urgent': return <ErrorOutline fontSize="small" sx={{ color: '#d32f2f' }} />;
    case 'high':   return <Warning fontSize="small" sx={{ color: '#f57c00' }} />;
    case 'medium': return <Info fontSize="small" sx={{ color: '#1976d2' }} />;
    default:       return <Circle fontSize="small" sx={{ color: '#bdbdbd', fontSize: 10 }} />;
  }
}

const HrInbox: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData]       = useState<InboxData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch]   = useState('');
  const [snack, setSnack]     = useState('');

  const load = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await apiFetch('/api/hr-inbox');
      if (!res.ok) throw new Error('Failed to load inbox');
      setData(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    load(true);
    setSnack('Refreshed');
  };

  const items = data?.items ?? [];

  const filtered = useMemo(() => {
    let result = items;
    if (priorityFilter) result = result.filter((i) => i.priority === priorityFilter);
    if (categoryFilter) result = result.filter((i) => i.category === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((i) =>
        i.title.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        i.employeeName?.toLowerCase().includes(q),
      );
    }
    return result;
  }, [items, priorityFilter, categoryFilter, search]);

  // Group by category
  const grouped = useMemo(() => {
    const map: Record<string, ActionItem[]> = {};
    for (const item of filtered) {
      if (!map[item.category]) map[item.category] = [];
      map[item.category].push(item);
    }
    return map;
  }, [filtered]);

  const urgentCount  = items.filter((i) => i.priority === 'urgent').length;
  const highCount    = items.filter((i) => i.priority === 'high').length;
  const categories   = Object.keys(data?.summary ?? {});

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Badge badgeContent={urgentCount} color="error" max={99}>
            <Inbox sx={{ fontSize: 32, color: 'primary.main' }} />
          </Badge>
          <Box>
            <Typography variant="h4" fontWeight={700}>HR Action Items</Typography>
            <Typography variant="body2" color="text.secondary">
              {items.length} item{items.length !== 1 ? 's' : ''} need{items.length === 1 ? 's' : ''} attention
              {urgentCount > 0 && ` · ${urgentCount} urgent`}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary chips by category */}
      {categories.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
          {categories.map((cat) => {
            const cfg = CATEGORY_CONFIG[cat];
            const count = data?.summary[cat] ?? 0;
            return (
              <Chip
                key={cat}
                icon={cfg?.icon as any}
                label={`${cat} (${count})`}
                onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
                variant={categoryFilter === cat ? 'filled' : 'outlined'}
                color={categoryFilter === cat ? 'primary' : 'default'}
                sx={{ borderColor: cfg?.color }}
              />
            );
          })}
        </Box>
      )}

      {/* KPI cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Urgent', count: urgentCount, color: '#d32f2f', bg: '#ffebee', priority: 'urgent' },
          { label: 'High',   count: highCount,   color: '#f57c00', bg: '#fff3e0', priority: 'high' },
          { label: 'Total',  count: items.length, color: '#1976d2', bg: '#e3f2fd', priority: '' },
          { label: 'Categories', count: categories.length, color: '#388e3c', bg: '#e8f5e9', priority: '' },
        ].map(({ label, count, color, bg, priority }) => (
          <Grid key={label} size={{ xs: 6, sm: 3 }}>
            <Card
              sx={{ bgcolor: bg, border: `2px solid ${color}20`, cursor: priority ? 'pointer' : 'default' }}
              onClick={() => priority && setPriorityFilter(priorityFilter === priority ? '' : priority)}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                <Typography variant="h3" fontWeight={800} sx={{ color }}>{count}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <ToggleButtonGroup value={priorityFilter} exclusive onChange={(_, v) => setPriorityFilter(v ?? '')} size="small">
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="urgent" sx={{ color: '#d32f2f' }}>🔴 Urgent</ToggleButton>
          <ToggleButton value="high"   sx={{ color: '#f57c00' }}>🟠 High</ToggleButton>
          <ToggleButton value="medium" sx={{ color: '#1976d2' }}>🔵 Medium</ToggleButton>
          <ToggleButton value="low"    sx={{ color: '#757575' }}>⚪ Low</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small" placeholder="Search…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><FilterList fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 180 }}
        />
        {(priorityFilter || categoryFilter || search) && (
          <Button size="small" onClick={() => { setPriorityFilter(''); setCategoryFilter(''); setSearch(''); }}>
            Clear filters
          </Button>
        )}
      </Box>

      {/* Action items grouped by category */}
      {filtered.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="h6">
            {items.length === 0 ? '🎉 All clear! No action items right now.' : 'No items match your current filters.'}
          </Typography>
        </Paper>
      ) : (
        Object.entries(grouped).map(([category, catItems]) => {
          const cfg = CATEGORY_CONFIG[category] ?? { icon: <Info />, color: '#9e9e9e' };
          return (
            <Box key={category} mb={3}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Box sx={{ color: cfg.color, display: 'flex' }}>{cfg.icon}</Box>
                <Typography variant="subtitle1" fontWeight={700}>{category}</Typography>
                <Chip label={catItems.length} size="small" sx={{ bgcolor: cfg.color, color: '#fff', fontWeight: 700, height: 20 }} />
              </Box>
              <Paper variant="outlined">
                {catItems.map((item, idx) => {
                  const pcfg = PRIORITY_CONFIG[item.priority];
                  return (
                    <React.Fragment key={item.id}>
                      {idx > 0 && <Divider />}
                      <CardActionArea
                        onClick={() => navigate(item.linkPath)}
                        sx={{
                          px: 2, py: 1.5,
                          borderLeft: `4px solid ${pcfg.color}`,
                          bgcolor: item.priority === 'urgent' ? '#fff8f8' : 'transparent',
                          '&:hover': { bgcolor: `${pcfg.color}08` },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <PriorityIcon p={item.priority} />
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {item.description}
                            </Typography>
                          </Box>
                          <Chip label={pcfg.label} color={pcfg.chip} size="small" variant="outlined" />
                          <NavigateNext fontSize="small" color="action" />
                        </Box>
                      </CardActionArea>
                    </React.Fragment>
                  );
                })}
              </Paper>
            </Box>
          );
        })
      )}

      <Snackbar open={!!snack} autoHideDuration={2000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default HrInbox;
